import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableProps,
  TableRow,
  Typography,
} from '@mui/material';
import { IconCaretRight } from 'assets/img';
import { theme } from 'redesign/theme/theme';

type PeriodTableSectionProps = {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
};

export const PeriodTableSection = ({
  title,
  isExpanded,
  onToggle,
  children,
}: PeriodTableSectionProps) => (
  <StyledAccordion expanded={isExpanded} onChange={onToggle}>
    <StyledAccordionSummary
      expandIcon={
        <SectionStateIcon isCollapsed={!isExpanded}>
          <IconCaretRight width={16} height={16} />
        </SectionStateIcon>
      }
    >
      <Typography variant="h3">{title}</Typography>
    </StyledAccordionSummary>
    <StyledAccordionDetails>
      <TableContainer>
        <StyledTable size="small">
          <StyledColgroup />
          <StyledTableHead>
            <TableRow>
              <StyledDaysCell>Day</StyledDaysCell>
              <StyledTableCell>Start Time</StyledTableCell>
              <StyledTableCell>End Time</StyledTableCell>
              <StyledTableCell>ORT</StyledTableCell>
              <StyledTableCell align="right">Actions</StyledTableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>{children}</TableBody>
        </StyledTable>
      </TableContainer>
    </StyledAccordionDetails>
  </StyledAccordion>
);

const StyledDaysCell = styled(TableCell)({
  fontWeight: 'bold',
  width: '30%',
  verticalAlign: 'top',
  textTransform: 'capitalize',
});
const StyledTableCell = styled(TableCell)({
  fontWeight: 'bold',
  width: '20%',
});
const StyledAccordion = styled(Accordion)<{ expanded: boolean }>(() => ({
  marginBottom: '16px',
  justifyContent: 'flex-start',
  boxShadow: 'none',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '&.MuiAccordion-root:before': {
    backgroundColor: 'white',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  flexDirection: 'row-reverse',
  gap: theme.spacing(2),
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0),
}));

const SectionStateIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCollapsed',
})<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  width: theme.spacing(6),
  height: theme.spacing(6),
  backgroundColor: isCollapsed ? theme.palette.lwPrimary[10] : 'white',
  borderRadius: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledColgroup = () => (
  <colgroup>
    <BaseCol />
    <DividerCol />
    <BaseCol />
    <BaseCol />
    <BaseCol />
  </colgroup>
);
const StyledTableHead = styled(TableHead)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.divider}`,
}));

const StyledTable = styled(Table)<TableProps>({
  tableLayout: 'fixed',
  '& .MuiTableCell-root': {
    fontSize: 14,
    color: 'black',
    padding: '12px 8px',
  },
});

const BaseCol = () => <col />;
const DividerCol = () => <col style={{ borderLeft: `1px solid ${theme.palette.divider}` }} />;
